import { forwardRef, useId } from 'react';
import styled, { css } from 'styled-components';
import { Spacing4, Spacing12, Spacing16, Spacing40 } from 'autogen/design-tokens/tokens';
import { Typography } from 'components/generic/typography';
import HelperText from '../components/HelperText';
import type { BaseInputProps } from '../shared/types';
import { InvalidInputFocusStyle, InvalidInputStyle, ValidInputDisabledStyle } from '../theme';

const Container = styled.div`
    display: inline-flex;
    flex-direction: column;
    position: relative;
`;

const LabelContainer = styled.label`
       display: inline-flex;
       flex-direction: column;
       gap: ${Spacing4};
       margin-bottom: 0;
       min-width: 0;
`;

const getSize = (size?: Size) => {
    switch (size) {
        case 'M':
            return css`
            height: 40px;
            padding: 0 ${Spacing12};
       `;
        case 'L':
            return css`
            height: 48px;
            padding: 0 ${Spacing16};
        `;
        default:
            return css`
               height: 40px;
               padding: 0 ${Spacing12};
        `;
    }
};

export type BaseTextInputStylingProps = { $size?: Size; $isInvalid?: ControlledTextInput['isInvalid'] };

export const baseTextInputStyling = css<BaseTextInputStylingProps>`
    border: ${({ theme }) => `1px solid ${theme.ColorBorderPrimary};`};
    border-radius: ${({ theme }) => theme.RadiusMedium};
    
    ${({ $size }) => getSize($size)};


    &:hover {
        border-color: ${({ theme }) => theme.ColorBorderHover};
    }

    
    &:focus {
        border-color: ${({ theme }) => theme.ColorBorderFocus};
        outline-offset: 0;
        outline-width: 1px;
    }

    &:disabled {
        ${ValidInputDisabledStyle};
    }

    &::placeholder {
        color: ${({ theme }) => theme.ColorTextDisabled};
    }

    ${({ $isInvalid }) =>
        $isInvalid &&
        css`
            &&,
            &&:hover {
                ${InvalidInputStyle};
            }

            &&:focus {
                ${InvalidInputFocusStyle};
            }
        `};
`;

const IconContainer = styled.div<{ left?: boolean; right?: boolean; $disabled?: boolean }>`
    position: absolute;
    top: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;

    & > svg {
        fill: ${({ theme, $disabled }) => ($disabled ? theme.ColorIconDisabled : theme.ColorIconPrimary)};
    }

    ${({ left, right }) => {
        if (right) {
            return css`
                right: ${Spacing16};
        
            `;
        }

        if (left) {
            return css`
                left: ${Spacing16};
        
            `;
        }

        return null;
    }};
`;

const Input = styled.input<{
    hasLeftIcon: boolean;
    hasRightIcon: boolean;
    $size: Size;
    $isInvalid?: ControlledTextInput['isInvalid'];
}>`
    ${baseTextInputStyling};

    ${({ hasLeftIcon }) =>
        hasLeftIcon &&
        css`
            padding-left: ${Spacing40};
        `};
    ${({ hasRightIcon }) =>
        hasRightIcon &&
        css`
            padding-right: ${Spacing40};
        `};

`;

type Size = 'M' | 'L';

export type ControlledTextInput = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> &
    BaseInputProps & {
        leftIcon?: JSX.Element;
        rightIcon?: JSX.Element;
    } & { size?: Size };

const BaseInput = forwardRef<HTMLInputElement, ControlledTextInput>((props, ref) => {
    const { leftIcon, rightIcon, className, isInvalid, disabled, label, helperText, size = 'M', ...rest } = props;

    return (
        <Container className={className}>
            {leftIcon && (
                <IconContainer $disabled={disabled} left>
                    {leftIcon}
                </IconContainer>
            )}
            <Input
                ref={ref}
                type="text"
                $isInvalid={isInvalid}
                $size={size}
                hasLeftIcon={!!leftIcon}
                hasRightIcon={!!rightIcon}
                disabled={disabled}
                {...rest}
            />
            {rightIcon && (
                <IconContainer $disabled={disabled} right>
                    {rightIcon}
                </IconContainer>
            )}
        </Container>
    );
});

const TextInput = forwardRef<HTMLInputElement, ControlledTextInput>((props, ref): JSX.Element => {
    const { leftIcon, rightIcon, className, isInvalid, disabled, label, helperText, size = 'M', ...rest } = props;
    const helperTextId = useId();

    const inputProps = {
        ref,
        leftIcon,
        rightIcon,
        isInvalid,
        disabled,
        helperText,
        size
    };

    if (label || helperText) {
        return (
            <LabelContainer className={className}>
                {label && (
                    <Typography.Label size="S" strong>
                        {label}
                    </Typography.Label>
                )}

                <BaseInput aria-describedby={helperTextId} {...inputProps} {...rest} />

                {helperText && <HelperText helperText={helperText} helperTextId={helperTextId} />}
            </LabelContainer>
        );
    }

    return <BaseInput className={className} {...inputProps} {...rest} />;
});

export default TextInput;
